import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { CircleCheck, RotateCw } from 'lucide-react';
import api from '../../services/api';
import { CountBadge, DefinitionList, ErrorState, IconTile, SectionCard, TableSkeletonRows } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { MISSING, formatDateRange } from '../../utils/format';
import { MissingValue } from './shared';

interface UnmatchedEmailItem {
  id: string;
  organizer_email: string | null;
  subject: string | null;
  start_date: string | null;
  end_date: string | null;
  reason: string;
  outlook_event_id?: string | null;
}

interface UnmatchedEventsCardProps {
  employees: any[];
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

const COLUMN_COUNT = 5;
const SERVICE_DOWN = 'The calendar service didn’t respond. Try again in a minute.';

/** Search the employee picker by name or email (HR usually has the organizer's email). */
const filterEmployees = createFilterOptions<any>({
  stringify: (emp) => `${emp.name || ''} ${emp.email || ''}`,
});

/**
 * Plain-language copy for a failed request. Raw server text can carry internals
 * (database or token errors), so it goes to the console, never the screen.
 */
const describeError = (err: any, action: 'load' | 'match'): string => {
  const status: number | undefined = err?.response?.status;
  const raw = String(err?.response?.data?.error || err?.message || '');
  console.error(`Unmatched calendar events: couldn’t ${action}.`, raw || err);
  if (status === 401) return 'Your session expired. Sign in again, then try again.';
  if (status === 403) return 'Your role can’t match calendar events. Ask an admin for HR access.';
  if (action === 'match') {
    if (status === 404 && /employee/i.test(raw)) return 'That employee no longer exists. Choose someone else.';
    if (status === 404) return 'This event was already matched or removed. Refresh the list.';
    if (status === 409 && /VP review queue/i.test(raw)) {
      return 'This event is already waiting on a VP review. Finish or withdraw that review, then match it.';
    }
    if (status === 409 && /VP-reviewed/i.test(raw)) {
      return 'This event is tied to a completed VP review. Change that request through HR instead.';
    }
    if (status === 409) return 'This event changed while it was being matched. Refresh the list and try again.';
    if (status === 400 && /date/i.test(raw)) {
      return 'This event has no valid dates. Fix it in Outlook, then sync the calendar again.';
    }
  }
  return SERVICE_DOWN;
};

const RefreshButton: React.FC<{ onClick: () => void; busy: boolean }> = ({ onClick, busy }) => (
  <Tooltip title="Refresh unmatched events">
    <span>
      <IconButton aria-label="Refresh unmatched events" onClick={onClick} disabled={busy} aria-busy={busy || undefined}>
        <RotateCw />
      </IconButton>
    </span>
  </Tooltip>
);

/**
 * Outlook out-of-office events whose organizer couldn't be matched to an
 * employee. HR matches each one by hand so it counts as time off.
 */
const UnmatchedEventsCard: React.FC<UnmatchedEventsCardProps> = ({ employees, onNotify }) => {
  const [emails, setEmails] = React.useState<UnmatchedEmailItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [matchError, setMatchError] = React.useState<string | null>(null);
  const [selectedItem, setSelectedItem] = React.useState<UnmatchedEmailItem | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState('');
  const [isMatching, setIsMatching] = React.useState(false);

  const fetchEmails = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/calendar/unmatched-emails');
      setEmails((res.data?.data?.items || []) as UnmatchedEmailItem[]);
    } catch (err: any) {
      setError(describeError(err, 'load'));
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, []);

  React.useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const employeeOptions = React.useMemo(
    () =>
      [...(employees || [])]
        .filter((emp: any) => !emp.employmentStatus || emp.employmentStatus === 'active')
        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '')),
    [employees]
  );

  const suggestedEmployeeId = React.useMemo(() => {
    if (!selectedItem) return '';
    const email = (selectedItem.organizer_email || '').toLowerCase();
    return employeeOptions.find((emp: any) => (emp.email || '').toLowerCase() === email)?._id || '';
  }, [selectedItem, employeeOptions]);

  const handleOpenMatchDialog = (item: UnmatchedEmailItem) => {
    setSelectedItem(item);
    setMatchError(null);
    const email = (item.organizer_email || '').toLowerCase();
    const exactMatch = employeeOptions.find((emp: any) => (emp.email || '').toLowerCase() === email);
    setSelectedEmployeeId(exactMatch?._id || '');
  };

  const handleCloseMatchDialog = () => {
    if (isMatching) return;
    setSelectedItem(null);
    setSelectedEmployeeId('');
    setMatchError(null);
  };

  const handleMatch = async () => {
    if (!selectedItem || !selectedEmployeeId) return;
    setIsMatching(true);
    setMatchError(null);
    try {
      const matchRes = await api.post(`/calendar/unmatched-emails/${selectedItem.id}/match`, {
        employeeId: selectedEmployeeId,
      });
      const matchedName = matchRes?.data?.data?.matchedEmployee?.name || 'the employee';
      onNotify(`Matched the event to ${matchedName}.`, 'success');
      setSelectedItem(null);
      setSelectedEmployeeId('');
      await fetchEmails();
    } catch (err: any) {
      setMatchError(describeError(err, 'match'));
    } finally {
      setIsMatching(false);
    }
  };

  const selectedEmployee = employeeOptions.find((emp: any) => emp._id === selectedEmployeeId) ?? null;
  // Skeleton rows only before the first response; later refreshes keep the
  // current layout and show progress on the refresh button alone.
  const initialLoad = !loaded;

  // Nothing waiting: one quiet line instead of an empty table.
  if (loaded && !error && emails.length === 0) {
    return (
      <SectionCard dense aria-label="Unmatched calendar events">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconTile>
            <CircleCheck />
          </IconTile>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
              Every calendar event is matched
            </Typography>
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
              Out-of-office events we can’t match to an employee appear here for you to assign.
            </Typography>
          </Box>
          <RefreshButton onClick={fetchEmails} busy={loading} />
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      id="unmatched-calendar-events"
      flush
      title={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          Unmatched calendar events
          {emails.length > 0 && <CountBadge count={emails.length} />}
        </Box>
      }
      description="Out-of-office events whose organizer isn’t matched to an employee. Match each one so it counts as time off."
      action={<RefreshButton onClick={fetchEmails} busy={loading} />}
    >
      {error && emails.length === 0 ? (
        <ErrorState
          compact
          title="Couldn’t load unmatched calendar events"
          body={error}
          onRetry={fetchEmails}
        />
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ m: 2, mb: 0 }}>
              Couldn’t refresh this list, so it shows the last events loaded. {error}
            </Alert>
          )}
          <Box sx={{ overflowX: 'auto' }}>
            <Table aria-label="Unmatched calendar events">
              <TableHead>
                <TableRow>
                  <TableCell>Organizer</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell>Event ID</TableCell>
                  <TableCell align="right">
                    <span className="sbg-sr-only">Actions</span>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {initialLoad ? (
                  <TableSkeletonRows columns={COLUMN_COUNT} rows={3} />
                ) : (
                  emails.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography variant="labelMd" component="div" sx={{ color: sbg.text.primary, overflowWrap: 'anywhere' }}>
                          {item.organizer_email || 'Unknown organizer'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 180, color: sbg.text.secondary }}>{item.subject || <MissingValue />}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateRange(item.start_date, item.end_date)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {item.outlook_event_id ? (
                          <Tooltip title={item.outlook_event_id}>
                            <Typography variant="mono" component="span" sx={{ color: sbg.text.tertiary }}>
                              {`${item.outlook_event_id.slice(0, 12)}…`}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <MissingValue />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenMatchDialog(item)}
                          aria-label={`Match event from ${item.organizer_email || 'unknown organizer'}`}
                        >
                          Match event
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </>
      )}

      <Dialog open={Boolean(selectedItem)} onClose={handleCloseMatchDialog} maxWidth="sm" fullWidth aria-labelledby="match-event-title">
        <DialogTitle id="match-event-title">Match calendar event</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <DefinitionList
                items={[
                  { label: 'Organizer', value: selectedItem.organizer_email || 'Unknown organizer' },
                  { label: 'Subject', value: selectedItem.subject || MISSING },
                  { label: 'Dates', value: formatDateRange(selectedItem.start_date, selectedItem.end_date) },
                ]}
              />
              <Autocomplete
                options={employeeOptions}
                value={selectedEmployee}
                onChange={(_, emp: any) => setSelectedEmployeeId(emp?._id || '')}
                getOptionLabel={(emp: any) => emp.name || ''}
                filterOptions={filterEmployees}
                noOptionsText="No active employee matches that name or email"
                isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
                renderOption={(props, emp: any) => {
                  const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                  return (
                    <li key={key} {...optionProps}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" component="div" sx={{ color: sbg.text.primary }}>
                          {emp.name}
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ color: sbg.text.secondary }}>
                          {emp.email}
                        </Typography>
                      </Box>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    placeholder="e.g. Maria Chen"
                    helperText={
                      selectedEmployeeId && selectedEmployeeId === suggestedEmployeeId
                        ? 'Suggested from the organizer’s email. Change it if it’s wrong.'
                        : 'Search by name or email, then choose who this event belongs to.'
                    }
                  />
                )}
              />
              {matchError && (
                <Alert severity="error">
                  Couldn’t match this event. {matchError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseMatchDialog} disabled={isMatching}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleMatch} disabled={!selectedEmployeeId || isMatching} aria-busy={isMatching || undefined}>
            {isMatching ? 'Matching…' : 'Match event'}
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export default UnmatchedEventsCard;
